import { useEffect, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrg } from '@/stores/org'

export type OrgRouteSyncState = 'loading' | 'switching' | 'synced'

/**
 * Tenant context in the URL — the `?orgId=` search param on protected routes.
 *
 * Contract (single mechanism; pages/links never hand-write `?orgId=`):
 * - Deep link / fresh navigation with a member org in the URL wins: the store
 *   switches to it (membership validated against the `GET /orgs` list; the
 *   backend still enforces the real boundary — a claimed org the session does
 *   not belong to answers 403 `org.NOT_A_MEMBER`, fail-closed).
 * - A param that is absent or not a member org converges to the active org, so
 *   every protected URL carries shareable, truthful org context.
 * - Store-driven org changes (header switcher, createOrg) converge the URL —
 *   they never get reverted by a stale param, because the honor branch only
 *   fires when `location.key` actually changed (`navChanged`).
 * - While orgs are loading or a URL→store switch is pending, the gate blocks
 *   the Outlet so org-scoped queries never fire against an unvalidated org.
 */
export function useOrgRouteSync(): OrgRouteSyncState {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const orgs = useOrg((s) => s.orgs)
  const activeOrgId = useOrg((s) => s.activeOrgId)
  const loading = useOrg((s) => s.loading)
  const switchOrg = useOrg((s) => s.switchOrg)
  const lastNavKey = useRef<string | null>(null)

  const urlOrgId = searchParams.get('orgId')
  const urlMember = urlOrgId !== null && orgs.some((o) => o.id === urlOrgId)
  // Read before the effect writes it back: true only for a navigation the
  // effect has not processed yet (deep link, in-app link, back/forward).
  // The marker has to be readable during render — the gate's state depends on
  // it — and a state mirror would re-run the effect whenever the marker
  // changes, which is exactly the stale-param revert this guard prevents.
  // oxlint-disable-next-line react/refs -- deliberate "last processed navigation" marker
  const navChanged = lastNavKey.current !== location.key

  useEffect(() => {
    if (loading) return
    if (navChanged) {
      lastNavKey.current = location.key
      if (urlOrgId && urlMember) {
        if (urlOrgId !== activeOrgId) switchOrg(urlOrgId)
        return
      }
    }
    const next = new URLSearchParams(searchParams)
    if (activeOrgId) next.set('orgId', activeOrgId)
    else next.delete('orgId')
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true })
  }, [loading, navChanged, urlOrgId, urlMember, activeOrgId, searchParams, location.key, switchOrg, setSearchParams])

  if (loading) return 'loading'
  if (navChanged && urlOrgId && urlMember && urlOrgId !== activeOrgId) return 'switching'
  return 'synced'
}

/**
 * Gates the console Outlet on org reconciliation — same visual language as
 * App.tsx RouteFallback (kept local to avoid an App ↔ components import cycle).
 */
export function OrgRouteGate({ children }: { children: React.ReactNode }) {
  const state = useOrgRouteSync()
  const { t } = useTranslation()
  if (state === 'synced') return <>{children}</>
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="flex min-h-[max(40vh,200px)] items-center justify-center p-8"
    >
      <div className="flex flex-col items-center gap-3 text-sm text-enterprise-muted">
        <div
          className="h-8 w-8 motion-safe:animate-spin motion-reduce:animate-none rounded-full border-2 border-enterprise-border border-t-enterprise-indigo"
          aria-hidden="true"
        />
        <span>{t('common.loading')}</span>
      </div>
    </div>
  )
}
